import type { CardTx } from './cards';

export type CardTransactionsData = { transactions: CardTx[] };

type CardTransactionsSnapshot = {
  data: CardTransactionsData;
  updatedAt: number;
};

const KEY_PREFIX = 'incpt-card-transactions-v1';

function cacheKey(userId: string | number, cardId: string): string {
  return `${KEY_PREFIX}:${encodeURIComponent(String(userId))}:${encodeURIComponent(cardId)}`;
}

/** Last successful card history. It intentionally has no expiry: stale history
 * is safer than a false empty state during a provider outage, and every mount
 * still attempts a live refresh. */
export function readCardTransactionsCache(
  userId: string | number | null,
  cardId: string,
): CardTransactionsSnapshot | undefined {
  if (userId == null || typeof window === 'undefined') return undefined;
  const key = cacheKey(userId, cardId);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as CardTransactionsSnapshot;
    if (
      !parsed ||
      !Number.isFinite(parsed.updatedAt) ||
      !Array.isArray(parsed.data?.transactions)
    ) {
      return undefined;
    }
    return parsed;
  } catch {
    // A corrupt entry must not prevent the live request or app boot.
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Storage may be unavailable; the live request still works.
    }
    return undefined;
  }
}

/** Save only a completed, valid API response. Rejected provider/network
 * requests never reach this function, so they cannot erase the last good copy. */
export function writeCardTransactionsCache(
  userId: string | number | null,
  cardId: string,
  data: CardTransactionsData,
): void {
  if (userId == null || typeof window === 'undefined' || !Array.isArray(data?.transactions)) {
    return;
  }
  try {
    window.localStorage.setItem(
      cacheKey(userId, cardId),
      JSON.stringify({ data, updatedAt: Date.now() } satisfies CardTransactionsSnapshot),
    );
  } catch {
    // Quota/private-mode failures must not break the live history.
  }
}
